"use client";

import { useEffect, useState } from "react";
import { usePathname, useRouter } from "next/navigation";
import { useChatNav } from "./ChatShell";
import {
  hideTip,
  SERVER_TIP,
  showTip,
  type Tip,
} from "@/lib/menu/Tooltip";
import { useMenu } from "@/lib/menu/MenuProvider";
import { menus } from "@/lib/menu/menus";
import type { ComponentType, SVGProps } from "react";
import { SERVER } from "./data";
import { CompassIcon, HomeIcon, PlusIcon } from "./icons";

/* shared hover-tip wiring for rail tiles */
const tipProps = (tip: Tip) => ({
  onPointerEnter: (e: React.PointerEvent<HTMLElement>) =>
    showTip(tip, e.currentTarget, "right"),
  onPointerLeave: hideTip,
});

/* home tile — takes you back to your DMs */
function HomeTile({ open, onOpen }: { open: boolean; onOpen(): void }) {
  return (
    <button
      type="button"
      aria-label="Home, Direct Messages"
      aria-current={open || undefined}
      className={`chat-tile tile-solid${open ? " tile-active" : ""}`}
      onClick={() => {
        hideTip();
        onOpen();
      }}
      {...tipProps({ title: "Home", icon: HomeIcon })}
    >
      <HomeIcon className="h-11 w-11" />
    </button>
  );
}

/* DM ping tile — skeleton avatar with an unread badge; opening the DM
   plays the dismissal animation, then the ping unmounts */
function PingTile({
  count,
  src,
  closing,
  onOpen,
}: {
  count: number;
  src: string;
  closing: boolean;
  onOpen(): void;
}) {
  return (
    <button
      type="button"
      aria-label={`Direct message, ${count} unread`}
      disabled={closing}
      className={`chat-tile tile-unread${closing ? " animate-tile-bye" : ""}`}
      onClick={() => {
        hideTip();
        onOpen();
      }}
      {...tipProps("Direct message")}
    >
      <span className="relative">
        <span
          aria-hidden
          className="block h-11 w-11 rounded-xl bg-panel-hi bg-cover bg-center"
          style={{ backgroundImage: `url(${src})` }}
        />
        <span
          aria-hidden
          className="absolute -right-1 -bottom-1 grid h-4 min-w-4 place-items-center rounded-full bg-dnd px-1 text-[9px] font-bold text-white ring-2 ring-chrome"
        >
          {count}
        </span>
      </span>
    </button>
  );
}

/* icon tile for the bottom actions */
function ActionTile({
  label,
  Icon,
  menu: menuId,
}: {
  label: string;
  Icon: ComponentType<SVGProps<SVGSVGElement>>;
  menu: keyof typeof menus;
}) {
  const { popup } = useMenu();
  return (
    <button
      type="button"
      aria-label={label}
      className="chat-tile tile-solid"
      onClick={() => {
        hideTip();
        popup(menus[menuId]);
      }}
      {...tipProps({ title: label, icon: Icon })}
    >
      <Icon className="h-8 w-8" />
    </button>
  );
}

export function ServerRail({ mobile = false }: { mobile?: boolean }) {
  /* stable ids — index removal would target the wrong ping if two close
     before their exit animations finish */
  const router = useRouter();
  const pathname = usePathname();
  const nav = useChatNav();
  const [pings, setPings] = useState([
    { id: "milk", count: 3, src: "/dm1.jpg", href: "/app/d/milk" },
    { id: "gravel", count: 9, src: "/dm2.jpg", href: "/app/d/gravel" },
    { id: "gc", count: 6, src: "/gc.jpg", href: "/app/g/1034567890123456789" },
  ]);
  const [closing, setClosing] = useState<string[]>([]);
  /* which tile's lit — the route is the source of truth now that the
     server tile actually navigates */
  const open = pathname.startsWith("/s/") ? "server" : "home";

  /* bye animation, then unmount — 220ms matches --animate-tile-bye */
  const dismiss = (id: string) => {
    setClosing((c) => (c.includes(id) ? c : [...c, id]));
    setTimeout(() => setPings((l) => l.filter((x) => x.id !== id)), 220);
  };

  /* a ping dies when its conversation is read (the route opens — dm bar,
     rail tile, wherever) or when the dm is closed from the bar.
     Deferred a tick: sync setState in an effect body would just
     re-render twice anyway */
  useEffect(() => {
    const hit = pings.find(
      (p) => p.href === pathname || nav.closedDms.has(p.id),
    );
    if (!hit || closing.includes(hit.id)) return;
    const t = setTimeout(() => dismiss(hit.id), 0);
    return () => clearTimeout(t);
  }, [pathname, nav.closedDms, pings, closing]);

  /* ping click → route to the channel + dismiss the ping (read it);
     mobile also slides the pager to the chat page */
  const openPing = (p: { id: string; href: string }) => {
    router.push(p.href);
    dismiss(p.id);
    if (mobile) nav.goToPage(1);
  };

  return (
    /* desktop — the rail ends where the profile bar starts, so bottom
       actions use mt-auto against a real edge instead of a px guess.
       mobile has no profile bar over the rail: full height, true bottom. */
    <nav
      aria-label="Servers"
      className={`chat-rail${mobile ? "" : " mb-[var(--profile-h)]"}`}
    >
      {/* NoServerShit — home + DM pings */}
      <div className="flex flex-col gap-[5px] px-2.5 py-[5px]">
        <HomeTile
          open={open === "home"}
          onOpen={() => router.push("/app/")}
        />
        {pings.map((p) => (
          <PingTile
            key={p.id}
            count={p.count}
            src={p.src}
            closing={closing.includes(p.id)}
            onOpen={() => openPing(p)}
          />
        ))}
      </div>

      <span aria-hidden className="mx-2.5 h-px shrink-0 bg-line" />

      {/* servers */}
      <div className="scroll-none flex min-h-0 flex-1 flex-col gap-[5px] overflow-y-auto px-2.5 py-[5px]">
        <button
          type="button"
          aria-current={open === "server" || undefined}
          className={`chat-tile${open === "server" ? " tile-active" : ""}`}
          onClick={() => {
            hideTip();
            /* routes to /s/<server>, which lands on the first channel —
               the server card moved to the channel column's header */
            router.push(`/s/${SERVER.id}`);
            if (mobile) nav.goToPage(1);
          }}
          {...tipProps(SERVER_TIP)}
        >
          <span
            aria-hidden
            className="block h-11 w-11 rounded-xl bg-cover bg-center"
            style={{ backgroundImage: "url(/favicon.webp)" }}
          />
        </button>
      </div>

      {/* bottom actions — mt-auto against the rail's real bottom edge */}
      <div className="mt-auto flex flex-col gap-[5px] px-2.5 py-[5px]">
        <ActionTile label="Discover" Icon={CompassIcon} menu="discover" />
        <ActionTile label="Create or join a server" Icon={PlusIcon} menu="createServer" />
      </div>
    </nav>
  );
}
